import {
  createSprintSchema,
  startSprintSchema,
  taskKeySchema,
  updateTeamSchema,
  type Sprint,
  type Task,
} from '@nexo/types';
import { Button, DatePicker, Field, Input, Modal, Select, Textarea, useToast } from '@nexo/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type SubmitEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useTeam } from '../../layouts/TeamContext';
import { api } from '../../lib/api';
import { isoDateFromToday } from '../../lib/format';
import { errorMessage, type FieldErrors, validate } from '../../lib/forms';
import { keys } from '../../lib/queries';

/** Default sprint length, as in Jira. */
const SPRINT_DAYS = 14;

function useSprintMutation<T>(fn: (input: T) => Promise<unknown>, onDone: () => void) {
  const { team } = useTeam();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.sprints(team.id) });
      void queryClient.invalidateQueries({ queryKey: keys.tasks(team.id) });
      onDone();
    },
  });
}

/** Creates a sprint or edits its name, goal and dates. */
export function SprintFormDialog({
  sprint,
  defaultName,
  open,
  onClose,
}: {
  sprint?: Sprint;
  defaultName?: string;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { team } = useTeam();
  const [values, setValues] = useState({
    name: sprint?.name ?? defaultName ?? '',
    goal: sprint?.goal ?? '',
    startDate: sprint?.startDate ?? '',
    endDate: sprint?.endDate ?? '',
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const save = useSprintMutation(
    (input: Parameters<typeof api.sprints.create>[1]) =>
      sprint ? api.sprints.update(sprint.id, input) : api.sprints.create(team.id, input),
    onClose,
  );

  function onSubmit(event: SubmitEvent) {
    event.preventDefault();
    const input = {
      name: values.name,
      goal: values.goal.trim() || null,
      startDate: values.startDate || null,
      endDate: values.endDate || null,
    };
    const result = validate(createSprintSchema, input);
    if (result.errors) return setErrors(result.errors);
    save.mutate(input, { onError: (err) => setErrors({ _: errorMessage(err) }) });
  }

  const set = (patch: Partial<typeof values>) => {
    setValues((v) => ({ ...v, ...patch }));
    setErrors({});
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={sprint ? t('Editar sprint') : t('Crear sprint')}
      size="sm"
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <Field label={t('Nombre')} error={errors.name}>
          {(props) => (
            <Input
              {...props}
              autoFocus
              maxLength={80}
              value={values.name}
              onChange={(e) => set({ name: e.target.value })}
            />
          )}
        </Field>
        <Field
          label={t('Objetivo')}
          hint={t('Opcional: qué quiere conseguir el equipo en este sprint')}
        >
          {(props) => (
            <Textarea
              {...props}
              rows={2}
              maxLength={500}
              value={values.goal}
              onChange={(e) => set({ goal: e.target.value })}
            />
          )}
        </Field>
        <DateFields
          startDate={values.startDate}
          endDate={values.endDate}
          error={errors.endDate}
          onChange={set}
        />
        <DialogFooter
          error={errors._}
          onCancel={onClose}
          submitLabel={sprint ? t('Guardar') : t('Crear sprint')}
          loading={save.isPending}
        />
      </form>
    </Modal>
  );
}

/** Starts a planned sprint (there can only be one active sprint). */
export function StartSprintDialog({
  sprint,
  tasks,
  open,
  onClose,
}: {
  sprint: Sprint;
  tasks: Task[];
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [dates, setDates] = useState({
    startDate: sprint.startDate ?? isoDateFromToday(0),
    endDate: sprint.endDate ?? isoDateFromToday(SPRINT_DAYS),
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const start = useSprintMutation(
    (input: typeof dates) => api.sprints.start(sprint.id, input),
    onClose,
  );
  const points = tasks.reduce((sum, task) => sum + (task.storyPoints ?? 0), 0);

  function onSubmit(event: SubmitEvent) {
    event.preventDefault();
    const result = validate(startSprintSchema, dates);
    if (result.errors) return setErrors(result.errors);
    start.mutate(dates, { onError: (err) => setErrors({ _: errorMessage(err) }) });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('Iniciar {{name}}', { name: sprint.name })}
      description={`${
        tasks.length === 1 ? t('1 tarea') : t('{{count}} tareas', { count: tasks.length })
      } · ${points === 1 ? t('1 punto') : t('{{count}} puntos', { count: points })}`}
      size="sm"
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <DateFields
          {...dates}
          required
          error={errors.endDate ?? errors.startDate}
          onChange={(patch) => {
            setDates((d) => ({ ...d, ...patch }));
            setErrors({});
          }}
        />
        <DialogFooter
          error={errors._}
          onCancel={onClose}
          submitLabel={t('Iniciar sprint')}
          loading={start.isPending}
        />
      </form>
    </Modal>
  );
}

/** Completes the active sprint and decides where its unfinished tasks go. */
export function CompleteSprintDialog({
  sprint,
  tasks,
  planned,
  open,
  onClose,
}: {
  sprint: Sprint;
  tasks: Task[];
  planned: Sprint[];
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [moveTo, setMoveTo] = useState('backlog');
  const [review, setReview] = useState('');
  const [error, setError] = useState<string>();
  const complete = useSprintMutation(
    () => api.sprints.complete(sprint.id, { moveTo, review: review.trim() || null }),
    () => {
      toast({ title: t('{{name}} completado', { name: sprint.name }), tone: 'success' });
      onClose();
    },
  );
  const done = tasks.filter((task) => task.status === 'done');
  const pending = tasks.length - done.length;
  const donePoints = done.reduce((sum, task) => sum + (task.storyPoints ?? 0), 0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('Completar {{name}}', { name: sprint.name })}
      size="sm"
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          complete.mutate(undefined, { onError: (err) => setError(errorMessage(err)) });
        }}
        className="flex flex-col gap-4"
      >
        <p className="text-sm text-muted">
          {done.length === 1 ? (
            <Trans
              i18nKey="Se ha completado <b>1</b> tarea ({{points}} puntos)."
              values={{ points: donePoints }}
              components={{ b: <strong className="text-text" /> }}
            />
          ) : (
            <Trans
              i18nKey="Se han completado <b>{{count}}</b> tareas ({{points}} puntos)."
              values={{ count: done.length, points: donePoints }}
              components={{ b: <strong className="text-text" /> }}
            />
          )}{' '}
          {pending === 0 ? (
            t('No queda nada pendiente.')
          ) : pending === 1 ? (
            <Trans
              i18nKey="Queda <b>1</b> sin terminar."
              components={{ b: <strong className="text-text" /> }}
            />
          ) : (
            <Trans
              i18nKey="Quedan <b>{{count}}</b> sin terminar."
              values={{ count: pending }}
              components={{ b: <strong className="text-text" /> }}
            />
          )}
        </p>
        {pending > 0 && (
          <Field label={t('Mover las tareas sin terminar a')}>
            {(props) => (
              <Select
                {...props}
                value={moveTo}
                onChange={setMoveTo}
                options={[
                  { value: 'backlog', label: t('Backlog') },
                  ...planned.map((s) => ({ value: s.id, label: s.name })),
                ]}
              />
            )}
          </Field>
        )}
        <Field
          label={t('Retrospectiva')}
          hint={t('Opcional: qué ha ido bien y qué mejorar. Se verá en Informes.')}
        >
          {(props) => (
            <Textarea
              {...props}
              rows={3}
              maxLength={5000}
              value={review}
              onChange={(e) => setReview(e.target.value)}
            />
          )}
        </Field>
        <DialogFooter
          error={error}
          onCancel={onClose}
          submitLabel={t('Completar sprint')}
          loading={complete.isPending}
        />
      </form>
    </Modal>
  );
}

/** Changes the prefix of the team's task keys (`NEX` in `NEX-42`). */
export function TaskKeyDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const { team } = useTeam();
  const queryClient = useQueryClient();
  const [taskKey, setTaskKey] = useState(team.taskKey);
  const [error, setError] = useState<string>();
  const save = useMutation({
    mutationFn: (key: string) => api.teams.update(team.id, { taskKey: key }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.teams });
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  function onSubmit(event: SubmitEvent) {
    event.preventDefault();
    const result = validate(updateTeamSchema, { taskKey });
    if (result.errors) return setError(result.errors.taskKey);
    save.mutate(taskKeySchema.parse(taskKey));
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('Clave de las tareas')}
      description={t(
        'Se usa en las claves como NEX-42; se pueden escribir en el chat para enlazar la tarea.',
      )}
      size="sm"
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <Field
          label={t('Clave')}
          error={error}
          hint={t('Así quedarán: {{example}}', { example: `${taskKey.toUpperCase() || '…'}-1` })}
        >
          {(props) => (
            <Input
              {...props}
              autoFocus
              maxLength={6}
              value={taskKey}
              onChange={(e) => {
                setTaskKey(e.target.value.toUpperCase());
                setError(undefined);
              }}
              className="font-mono uppercase"
            />
          )}
        </Field>
        <DialogFooter onCancel={onClose} submitLabel={t('Guardar')} loading={save.isPending} />
      </form>
    </Modal>
  );
}

function DateFields({
  startDate,
  endDate,
  error,
  required = false,
  onChange,
}: {
  startDate: string;
  endDate: string;
  error?: string | undefined;
  required?: boolean;
  onChange: (patch: { startDate?: string; endDate?: string }) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-2 gap-3">
      <Field label={t('Inicio', { context: 'sprintDate' })}>
        {(props) => (
          <DatePicker
            {...props}
            clearable={!required}
            value={startDate || null}
            onChange={(value) => onChange({ startDate: value ?? '' })}
          />
        )}
      </Field>
      <Field label={t('Fin')} error={error}>
        {(props) => (
          <DatePicker
            {...props}
            clearable={!required}
            min={startDate || undefined}
            value={endDate || null}
            onChange={(value) => onChange({ endDate: value ?? '' })}
          />
        )}
      </Field>
    </div>
  );
}

function DialogFooter({
  error,
  onCancel,
  submitLabel,
  loading,
}: {
  error?: string | undefined;
  onCancel: () => void;
  submitLabel: string;
  loading: boolean;
}) {
  const { t } = useTranslation();
  return (
    <>
      {error && (
        <p role="alert" className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2 border-t border-border pt-4">
        <Button variant="ghost" onClick={onCancel}>
          {t('Cancelar')}
        </Button>
        <Button type="submit" loading={loading}>
          {submitLabel}
        </Button>
      </div>
    </>
  );
}
